import React, { useEffect, useMemo } from "react";
import { useParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { newForm, statusReady } from "data/form/formActions";
import forms from "lib/api/services/forms";
import TaskComplete from "./TaskComplete";
import TaskError from "./TaskError";
import useTask from "../../hooks/useTask";
import TaskForm from "components/Forms/TaskForm";
import { Workbench } from "components/Workbench";
import TaskContext, { ITaskContext } from "contexts/TaskContext";
import { Loading } from "@alm/flow-ds/components";
import ErrorBoundary from "components/Errors/ErrorBoundary";

interface RouteProps {
	tid: string;
}

function ActivityForm() {
	const { tid } = useParams<RouteProps>();
	const reduxDispatch = useDispatch();

	// Hook to get task actions
	const {
		state,
		close,
		complete,
		loadTask,
		save,
		saveAndClose,
		unassignTask,
		deleteActivity,
	} = useTask(tid);

	/**
	 * Busy flag
	 * The form is considered 'busy' when a global action is in process such as
	 * saving or submitting
	 */
	const busy = useMemo(() => {
		return state.saving || state.submitting;
	}, [state.saving, state.submitting]);

	/**
	 * Load the form associated with the task
	 */
	useEffect(() => {
		if (state.fid) {
			const fid = state.fid;
			forms.get(fid).then(form => {
				reduxDispatch(newForm(fid, form));
				reduxDispatch(statusReady(fid));
			});
		}
	}, [reduxDispatch, state.fid]);

	/**
	 * Loads the task
	 */
	useEffect(() => {
		loadTask();
	}, [loadTask]);

	// Task context that's passed to the context provider
	const taskContextValue: ITaskContext = useMemo(
		() => ({
			name: state.title,
			id: state.id,
			canBeUnassigned: state.canBeUnassigned,
			canBeReassigned: state.canBeReassigned,
			canSaveTask: state.canSaveTask,
			busy,
			save,
			close,
			complete,
			saveAndClose,
			unassignTask,
			validationErrors: state.validationErrors,
			canDeleteActivity: state.canDeleteActivity,
			deleteActivity,
			assignee: state.assignee,
			startDate: state.startDate,
			dueDate: state.dueDate,
		}),
		[
			state.title,
			state.id,
			state.canBeUnassigned,
			state.canBeReassigned,
			state.canSaveTask,
			state.validationErrors,
			state.canDeleteActivity,
			state.assignee,
			state.startDate,
			state.dueDate,
			busy,
			save,
			close,
			complete,
			saveAndClose,
			unassignTask,
			deleteActivity,
		]
	);

	// Task complete
	if (state.done) {
		return <TaskComplete />;
	}

	// Unexpected errors we can't recover from
	if (state.error) {
		return <TaskError error={state.error} />;
	}

	return (
		<TaskContext.Provider value={taskContextValue}>
			<Workbench>
				<ErrorBoundary>
					{state.ready && state.fid ? (
						<TaskForm fid={state.fid} workbenchIndex={0} />
					) : (
						<Loading className="elem__loader--lg mt-5" />
					)}
				</ErrorBoundary>
			</Workbench>
		</TaskContext.Provider>
	);
}

export default ActivityForm;
